/**
 * Header API Service
 * 
 * Fetches header menu structure from Strapi API
 * Handles locale-specific requests (en/zh) and authentication
 */

import { API_URL, Header_API_TOKEN } from '../config';
import { processHeaderResponse, ProcessedHeaderMenus } from './menuFlattener';

const HEADER_API_ENDPOINT = '/header';
const HEADER_POPULATE_PARAMS = 
  'populate[HeaderMenu][populate]=MenuItems,SubItems' +
  '&populate[MobileHeaderMenuLower][populate]=MenuItems,SubItems' +
  '&populate[WatchMenu][populate]=MenuItems,SubItems' +
  '&populate=Logo';

/**
 * Fetch header menu data from Strapi API
 * 
 * @param locale - Language locale ('en' or 'zh')
 * @returns Processed header menu structure
 * 
 * @example
 * const headerData = await fetchHeaderMenu('en');
 * console.log(headerData.desktopPrimary);
 */
export async function fetchHeaderMenu(locale: 'en' | 'zh' = 'en'): Promise<ProcessedHeaderMenus> {
  try {
    // Construct API URL with locale and populate parameters
    const apiLocale = locale === 'zh' ? 'zh-Hant-HK' : 'en';
    const url = `${API_URL}${HEADER_API_ENDPOINT}?locale=${apiLocale}&${HEADER_POPULATE_PARAMS}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${Header_API_TOKEN}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      console.error(`Failed to fetch header menu: ${response.status} ${response.statusText}`);
      throw new Error(`Header API request failed: ${response.status}`);
    }

    const data = await response.json();
    
    // Process and flatten the menu structure
    return processHeaderResponse(data);

  } catch (error) {
    console.error('Error fetching header menu from Strapi:', error);
    
    // Return empty menu structure on error
    return {
      desktopPrimary: [],
      mobilePrimary: [],
      mobileSecondary: [],
      watchDesktop: [],
      watchMobile: []
    };
  }
}

/**
 * Cache for header menu data to avoid redundant API calls
 */
const menuCache = new Map<string, { data: ProcessedHeaderMenus; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch header menu with caching
 * Reduces API calls by caching responses per locale
 * 
 * @param locale - Language locale ('en' or 'zh')
 * @returns Cached or fresh header menu data
 */
export async function fetchHeaderMenuCached(locale: 'en' | 'zh' = 'en'): Promise<ProcessedHeaderMenus> {
  const cacheKey = `header-menu-${locale}`;
  const cached = menuCache.get(cacheKey);

  // Return cached data if valid
  if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
    return cached.data;
  }

  // Fetch fresh data
  const data = await fetchHeaderMenu(locale);
  
  // Update cache
  menuCache.set(cacheKey, {
    data,
    timestamp: Date.now()
  });

  return data;
}
